/**
 * Buchungsstrecke B1 — Reihenfolge, Vorauswahl und „+ …“-Zeilen der Varianten auf der Gastseite
 * (docs/KONZEPT-VERKAUFSPSYCHOLOGIE.md § 4 Punkt 4, § 5, § 6; docs/LOOP-BUCHUNGSSTRECKE.md B1).
 *
 * Reine Funktionen ohne React, ohne Netz, ohne Modell. Beträge werden hier NIE gerechnet — nur
 * die vom Server gelieferten `amount_total_cents` VERGLICHEN (Leitprinzip: Beträge nur aus
 * `packages/pricing`).
 *
 * Die drei Regeln:
 *  1. **Reihenfolge**: absteigend nach Gesamtpreis. Hat der Betrieb bei genau drei Varianten eine
 *     Empfehlung gesetzt, steht sie in der Mitte — die übrigen bleiben absteigend darum herum.
 *  2. **Vorauswahl**: die Empfehlung des Betriebs (nur eine Vorauswahl der ANSICHT — keine Annahme,
 *     keine Zahlung; der Gast muss weiterhin selbst annehmen). Ohne Empfehlung bleibt die Auswahl
 *     bei mehreren Varianten leer, wie seit 17.09.2026.
 *  3. **„+ …“-Zeilen**: die Positionen, die eine Variante gegenüber der NÄCHSTKLEINEREN (nach
 *     Preis) zusätzlich hat, rein aus den Positionen abgeleitet. Nur wenn beide Varianten
 *     mindestens eine Position teilen — zwei völlig verschiedene Menüs sind kein „mehr“, dort
 *     wäre jede Zeile ein „+“ und damit keine Aussage.
 */

export interface EmpfehlungsVariante {
  id: string;
  amount_total_cents: number;
  is_recommended?: boolean | null;
  /** Migration 731: „À la carte“ (Abrechnung vor Ort, kein Betrag). */
  a_la_carte?: boolean | null;
}

export interface VergleichsPosition {
  name: string;
  section: string;
  is_optional?: boolean | null;
}

/** Der Positionstyp einer Variante — damit die Seite die übersetzten Namen der Positionen behält. */
export type PositionVon<T> = T extends { items?: readonly (infer P)[] | null } ? P : never;

/** Die EINE Empfehlung, oder `null` (keine oder — Altbestand/Fehler — mehrdeutig). */
export function empfehlungsId(optionen: readonly EmpfehlungsVariante[]): string | null {
  const empfohlen = optionen.filter((o) => o.is_recommended === true);
  return empfohlen.length === 1 ? empfohlen[0]!.id : null;
}

/** Migration 731: ist die Variante „À la carte“ (kein Betrag, Abrechnung vor Ort)? */
export function istALaCarte(o: { a_la_carte?: boolean | null }): boolean {
  return o.a_la_carte === true;
}

/**
 * Regel 1: absteigend nach Preis, Empfehlung bei drei Varianten in der Mitte. Migration 731: eine
 * „À la carte“-Variante steht immer am Ende (sie hat keinen Preis, der sie einordnet) und zählt
 * für die Mitte-Regel nicht mit.
 */
export function variantenReihenfolge<T extends EmpfehlungsVariante>(optionen: readonly T[]): T[] {
  const bepreist = optionen.filter((o) => !istALaCarte(o));
  const alaCarte = optionen.filter(istALaCarte);
  if (alaCarte.length === 0) return reihenfolgeBepreist(optionen);
  return [...reihenfolgeBepreist(bepreist), ...alaCarte];
}

function reihenfolgeBepreist<T extends EmpfehlungsVariante>(optionen: readonly T[]): T[] {
  const absteigend = optionen
    .map((o, i) => ({ o, i }))
    .sort((a, b) => b.o.amount_total_cents - a.o.amount_total_cents || a.i - b.i)
    .map((x) => x.o);
  const empfehlung = empfehlungsId(optionen);
  if (empfehlung == null || absteigend.length !== 3) return absteigend;
  const rest = absteigend.filter((o) => o.id !== empfehlung);
  const mitte = absteigend.find((o) => o.id === empfehlung)!;
  return [rest[0]!, mitte, rest[1]!];
}

/**
 * Regel 2: was vorausgewählt ist, solange der Gast noch nichts gewählt hat. Eine einzige Variante
 * ist das Angebot selbst (17.09.2026); bei mehreren nur die Empfehlung des Betriebs.
 */
export function vorauswahl(optionen: readonly EmpfehlungsVariante[]): string | null {
  if (optionen.length === 1) return optionen[0]!.id;
  // Migration 731: eine „À la carte“-Variante wird nie vorausgewählt, solange eine bepreiste daneben steht.
  const empfohlen = empfehlungsId(optionen);
  if (empfohlen != null && optionen.some((o) => o.id === empfohlen && istALaCarte(o)) && optionen.some((o) => !istALaCarte(o))) return null;
  return empfohlen;
}

function schluessel(p: VergleichsPosition): string {
  return `${p.section}\u0000${p.name.trim().replace(/\s+/g, " ").toLocaleLowerCase("de")}`;
}

/** Nur was im Preis steckt: keine Anpassungszeilen, keine optionalen Zubuchungen. */
function zaehlt(p: VergleichsPosition): boolean {
  return p.section !== "adjustment" && p.is_optional !== true && p.name.trim() !== "";
}

/**
 * Regel 3: je Variante die Positionen, die sie gegenüber der nächstkleineren zusätzlich hat.
 * Mengengenau als Multimenge (zweimal „Antipasti“ gegen einmal = ein „+ Antipasti“). Die
 * günstigste Variante bekommt nie Zeilen. Zurück kommen die Positionen selbst (die Seite zeigt
 * ihren übersetzten Namen), in der Reihenfolge der Variante.
 */
export function zusatzZeilen<T extends EmpfehlungsVariante & { items?: readonly VergleichsPosition[] | null }>(
  optionen: readonly T[],
): Map<string, PositionVon<T>[]> {
  type P = PositionVon<T>;
  const ergebnis = new Map<string, P[]>();
  // Migration 731: „À la carte“ hat keinen Preis, gegen den ein „+“ etwas aussagte.
  const aufsteigend = optionen
    .filter((o) => !istALaCarte(o))
    .map((o, i) => ({ o, i }))
    .sort((a, b) => a.o.amount_total_cents - b.o.amount_total_cents || a.i - b.i)
    .map((x) => x.o);
  for (let k = 1; k < aufsteigend.length; k++) {
    const groesser = ((aufsteigend[k]!.items ?? []) as readonly P[]).filter(zaehlt);
    const kleiner = (aufsteigend[k - 1]!.items ?? []).filter(zaehlt);
    // Gleicher Preis ist kein „mehr“.
    if (aufsteigend[k]!.amount_total_cents === aufsteigend[k - 1]!.amount_total_cents) continue;
    const rest = new Map<string, number>();
    for (const p of kleiner) rest.set(schluessel(p), (rest.get(schluessel(p)) ?? 0) + 1);
    const zusaetze: P[] = [];
    let gemeinsam = 0;
    for (const p of groesser) {
      const s = schluessel(p);
      const n = rest.get(s) ?? 0;
      if (n > 0) {
        rest.set(s, n - 1);
        gemeinsam++;
      } else {
        zusaetze.push(p);
      }
    }
    if (gemeinsam > 0 && zusaetze.length > 0) ergebnis.set(aufsteigend[k]!.id, zusaetze);
  }
  return ergebnis;
}
